
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface Student {
  code: string;
  first_name: string;
  last_name: string | null;
  nickname: string | null;
  hours: number | null;
  education_level: string | null;
  school: string | null;
}

export type SortField = 'code' | 'first_name' | 'nickname' | 'hours' | null;
export type SortOrder = 'asc' | 'desc';

interface UseStudentListProps {
  limit?: number;
}

export const useStudentList = ({ limit = 10 }: UseStudentListProps = {}) => {
  const [students, setStudents] = useState<Student[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('code');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  
  const totalPages = Math.ceil(totalStudents / limit);
  
  const fetchStudentCount = async (query: string) => {
    try {
      let countQuery = (supabase as any)
        .schema("student")
        .from('fishsix_students')
        .select('*', { count: 'exact', head: true })
        .is('deleted_at', null);
      
      if (query) {
        countQuery = countQuery.or(`first_name.ilike.%${query}%,last_name.ilike.%${query}%,nickname.ilike.%${query}%,code.ilike.%${query}%`);
      }
      
      const { count, error } = await countQuery;
      
      if (error) throw error;
      
      if (typeof count === 'number') {
        setTotalStudents(count);
      }
    } catch (error) {
      console.error('Error fetching student count:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถนับจำนวนนักเรียนได้',
        variant: 'destructive',
      });
    }
  };

  const fetchStudents = async () => {
    setIsLoading(true);
    try {
      let query = (supabase as any)
        .schema("student")
        .from('fishsix_students')
        .select('code, first_name, last_name, nickname, hours, education_level, school')
        .is('deleted_at', null);
      
      // Apply sorting if a sort field is selected
      if (sortField) {
        query = query.order(sortField, { ascending: sortOrder === 'asc' });
      }
      
      // Apply pagination
      query = query.range((currentPage - 1) * limit, currentPage * limit - 1);
      
      // Apply search if query exists
      if (searchQuery) {
        query = query.or(`first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,nickname.ilike.%${searchQuery}%,code.ilike.%${searchQuery}%`);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      setStudents(data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถดึงข้อมูลนักเรียนได้',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page on new search
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSort = (field: SortField) => {
    // If clicking on the same field, toggle the sort order
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking on a new field, set it as the sort field and default to ascending order
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page on new sort
  };

  useEffect(() => {
    fetchStudentCount(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    fetchStudents();
  }, [currentPage, searchQuery, sortField, sortOrder]);

  return {
    students,
    isLoading,
    currentPage,
    totalPages,
    searchQuery,
    sortField,
    sortOrder,
    handleSearch,
    handlePageChange,
    handleSort,
  };
};
